import { Trans, defineMessage, t } from "@lingui/macro"
import { array, either } from "fp-ts"
import { pipe } from "fp-ts/function"
import { NonEmptyArray } from "fp-ts/lib/NonEmptyArray"
import * as D from "io-ts/Decoder"
import { useRouter } from "next/router"
import { useState } from "react"

import { AuctionBlock } from "@/domains/auction-blocks/model"
import { usePastOrCurrentAuctionBlocks } from "@/domains/auction-blocks/query"
import { Sorting } from "@/domains/auctions/model"
import {
  useAuctionRecordsQuery,
  useAuctionsQuery,
} from "@/domains/auctions/query"
import * as Bar from "@/domains/auctions/view/bar"
import { AuctionsGrid } from "@/domains/auctions/view/grid"
import { AuctionsSlider } from "@/domains/auctions/view/slider"
import { SortingSelect } from "@/domains/auctions/view/sorting-select"
import { ViewSwitch } from "@/domains/auctions/view/view-switch"
import { useI18n } from "@/lib/i18n"
import { NumberFromString } from "@/lib/number"
import { CSS } from "@/stitches"
import * as Autocomplete from "@/ui/autocomplete"
import { Badge } from "@/ui/badge"
import { Box } from "@/ui/box"
import { Container } from "@/ui/container"
import * as Dialog from "@/ui/dialog"
import { FieldBase } from "@/ui/field-base"
import * as FilterDialog from "@/ui/filter-dialog"
import { PageNav } from "@/ui/page-nav"
import { Stack } from "@/ui/stack"
import { Text, Title } from "@/ui/text"
import { Throbber } from "@/ui/throbber"

import { Layout } from "../_layout"

function AuctionRecords() {
  const auctionRecordsQuery = useAuctionRecordsQuery()

  if (auctionRecordsQuery.data?.length === 0) {
    return null
  }

  return (
    <Box as="section" css={{ py: "$16" }}>
      <Container>
        <Title as="h2" css={{ mb: "$8" }}>
          <Trans id="auctionResults.records.title">Aukční rekordy</Trans>
        </Title>
      </Container>
      {auctionRecordsQuery.data && (
        <AuctionsSlider auctions={auctionRecordsQuery.data} />
      )}
    </Box>
  )
}

const defaultRouterQuery: {
  page: number
  sorting: Sorting
} = {
  page: 1,
  sorting: "priceDesc",
}

const RouterQuery = pipe(
  D.partial({
    page: NumberFromString,
    sorting: Sorting,
  }),
  D.map((params) => ({
    ...defaultRouterQuery,
    ...params,
  }))
)

type RouterQueryParams = D.TypeOf<typeof RouterQuery>

function useRouterQuery(): RouterQueryParams {
  const router = useRouter()

  return pipe(
    router.query,
    RouterQuery.decode,
    either.getOrElse(() => defaultRouterQuery)
  )
}

const itemsPerPage = 24

function AuctionBlockSelect({
  auctionBlocks,
  value,
  onChange,
  css,
}: {
  auctionBlocks: Array<AuctionBlock>
  value: NonEmptyArray<AuctionBlock> | null
  onChange: (value: NonEmptyArray<AuctionBlock> | null) => void
  css?: CSS
}) {
  const { localize, i18n } = useI18n()

  return (
    <Autocomplete.Root
      multiselect
      value={value ?? []}
      onValueChange={(blocks) =>
        onChange(array.isNonEmpty(blocks) ? blocks : null)
      }
      items={auctionBlocks}
      keyOf={(block) => block.id}
      filterPredicate={(search, block) =>
        (localize(block.name) ?? "")
          .toLowerCase()
          .includes(search.toLocaleLowerCase())
      }
      viewOf={(item) => (
        <Box>
          <Text as="p" size="xs" weight="medium" lineHeight="inherit">
            {localize(item.name)}
          </Text>
          <Text
            as="p"
            size="xs"
            color="muted"
            weight="medium"
            lineHeight="inherit"
          >
            {i18n.date(item.dateStart)}
            {item.dateLastAuctionEnd &&
              ` – ${i18n.date(item.dateLastAuctionEnd)}`}
          </Text>
        </Box>
      )}
    >
      <Autocomplete.Trigger
        placeholder={t({
          id: "auctionResults.auctionBlockSelect.placeholder",
          message: "Aukční celek",
        })}
        css={css}
        addonBefore={value ? <Badge>{value.length}</Badge> : undefined}
      >
        {value?.map((block) => localize(block.name)).join(", ")}
      </Autocomplete.Trigger>
    </Autocomplete.Root>
  )
}

function AllAuctions() {
  const router = useRouter()
  const { page, sorting } = useRouterQuery()
  const [selectedAuctionBlocks, setSelectedAuctionBlocks] =
    useState<NonEmptyArray<AuctionBlock> | null>(null)

  const auctionsQuery = useAuctionsQuery({
    isFinished: true,
    sorting,
    page,
    itemsPerPage,
    auctionBlock: selectedAuctionBlocks
      ? selectedAuctionBlocks.map((block) => block.id)
      : undefined,
  })

  const auctionBlocksQuery = usePastOrCurrentAuctionBlocks()

  const setSorting = (newSorting: Sorting) => {
    router.push({
      pathname: router.pathname,
      query: {
        page,
        sorting: newSorting,
      },
    })
  }

  return (
    <Box as="section">
      <Container>
        <Bar.Root css={{ mb: "$8" }}>
          <Bar.Left>
            <Title as="h2" id="all-items">
              <Trans id="auctionResults.allAuctions.title">Všechny aukce</Trans>
            </Title>
          </Bar.Left>
          <Bar.Right>
            <ViewSwitch />
            {auctionBlocksQuery.isSuccess ? (
              <>
                <Dialog.Root>
                  <FilterDialog.Trigger
                    count={selectedAuctionBlocks?.length}
                    css={{ ml: "auto", "@md": { display: "none" } }}
                  />
                  <Dialog.Content position="right" size="xs">
                    <Dialog.Header>
                      <Trans id="auctionResults.filters.title">Filtry</Trans>
                    </Dialog.Header>
                    <Dialog.Body>
                      <FieldBase
                        label={
                          <Trans id="auctionResults.filters.auctionBlock">
                            Aukční celek
                          </Trans>
                        }
                      >
                        <AuctionBlockSelect
                          auctionBlocks={auctionBlocksQuery.data}
                          value={selectedAuctionBlocks}
                          onChange={setSelectedAuctionBlocks}
                        />
                      </FieldBase>
                    </Dialog.Body>
                  </Dialog.Content>
                </Dialog.Root>
                <AuctionBlockSelect
                  auctionBlocks={auctionBlocksQuery.data}
                  value={selectedAuctionBlocks}
                  onChange={setSelectedAuctionBlocks}
                  css={{
                    display: "none",
                    "@md": { display: "flex" },
                    maxWidth: "16rem",
                  }}
                />
              </>
            ) : (
              <Throbber />
            )}
            <SortingSelect
              value={sorting}
              onValueChange={setSorting}
              labels={{
                default: defineMessage({
                  id: "auctions.sortBy.dateOfAuction",
                  message: "Datum dražby",
                }),
              }}
            />
          </Bar.Right>
        </Bar.Root>
        <AuctionsGrid
          loading={auctionsQuery.isLoading}
          auctions={auctionsQuery.data?.data}
        />
        {auctionsQuery.isSuccess && (
          <PageNav
            css={{ mt: "$12" }}
            page={page}
            totalPages={Math.ceil(auctionsQuery.data.totalItems / itemsPerPage)}
            hrefTemplate={(newPage) => ({
              pathname: "/results",
              query: {
                page: newPage,
                sorting,
              },
            })}
          />
        )}
      </Container>
    </Box>
  )
}

export default function AuctionResultsPage() {
  return (
    <Layout>
      <Stack direction="column" gap={16} css={{ mb: "$16" }}>
        <AuctionRecords />
        <AllAuctions />
      </Stack>
    </Layout>
  )
}
